import React, { useEffect, useState } from "react";
import { Lightbulb } from "lucide-react";
import { isGotIt, isSuggestionsFeatureEnabled } from "../lib/suggestions";
import { isPairingUrgencyEligible } from "../lib/suggestionPresentation";
import SuggestionsDrawer from "./SuggestionsDrawer";

/**
 * Small "Suggestions" trigger that lives on a close-dated inventory
 * row. Hidden when ANY of these are true:
 *   - the server says FEATURE_SUGGESTIONS is OFF (probed via
 *     ``GET /api/suggestions/status``; hidden until probe resolves)
 *   - the batch has been dismissed via "Got it" in the last 24h
 *   - the batch urgency is not "urgent" / "attention" (days 1–7)
 *
 * The label is exactly "Suggestions" — no AI/GPT/LLM/Freshness words.
 */
export function SuggestionsTrigger({ batch, className = "" }) {
  const [open, setOpen] = useState(false);
  // ``featureEnabled`` is ``null`` until the server-side flag has
  // resolved. We render NOTHING while null — safer-by-default so a
  // briefly-flashing trigger never appears in production when the
  // flag is off.
  const [featureEnabled, setFeatureEnabled] = useState(null);
  // Re-evaluate Got-It on mount / when batch changes / when drawer closes.
  const [dismissed, setDismissed] = useState(() => isGotIt(batch?.id));

  useEffect(() => {
    let cancelled = false;
    isSuggestionsFeatureEnabled().then((on) => {
      if (!cancelled) setFeatureEnabled(on);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setDismissed(isGotIt(batch?.id));
  }, [batch?.id]);

  useEffect(() => {
    if (!open) setDismissed(isGotIt(batch?.id));
  }, [open, batch?.id]);

  if (featureEnabled !== true) return null;
  if (!batch?.id) return null;
  if (!isPairingUrgencyEligible(batch.urgency)) return null;
  if (dismissed) return null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`h-8 px-2.5 border border-ink/40 bg-paper text-ink hover:bg-ink hover:text-paper transition-colors font-display text-[10px] uppercase tracking-wider rounded-sm flex items-center gap-1 ${className}`}
        data-testid={`suggestions-trigger-${batch.id}`}
        aria-label={`Suggestions for ${batch.product_name || batch.id}`}
      >
        <Lightbulb size={11} strokeWidth={2.5} />
        Suggestions
      </button>
      <SuggestionsDrawer
        open={open}
        onOpenChange={setOpen}
        batch={batch}
      />
    </>
  );
}

export default SuggestionsTrigger;
